import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FolderOpen, FolderPlus, KeyRound, Trash2 } from "lucide-react"
import { type FormEvent, useMemo, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { AccesosDeCarpeta } from "@/componentes/drive/AccesosDeCarpeta"
import { ArbolCarpetas } from "@/componentes/drive/ArbolCarpetas"
import { PanelArchivos } from "@/componentes/drive/PanelArchivos"
import { armarArbol, type Carpeta } from "@/componentes/drive/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El Drive de la empresa: la documentación de la agencia, no la del expediente.
 *
 * ⚠ **No es la compuerta documental y por eso está aparte.** Los documentos de un
 * expediente bloquean el avance del estatus y su tipo está reglado; aquí van
 * manuales, formatos y actas, que no bloquean nada. Mezclarlos habría metido la
 * compuerta en un almacén general o —peor— habría dejado entrar en el expediente
 * archivos que no pasan por ella.
 *
 * ⚠ **El permiso es de la CARPETA, no del módulo.** `drive:ver` abre la pantalla y no
 * abre ni una carpeta. Con un único permiso para todo, RRHH no podría guardar nada de
 * personal sin que lo viera operaciones, y entonces lo confidencial se queda fuera del
 * Drive: acaba en un correo o en un pendrive, que es lo que un almacén compartido
 * viene a evitar.
 *
 * ⚠ **Borrar una carpeta exige vaciarla antes.** Una cascada convierte una pulsación
 * en la desaparición de una rama entera, y en un almacén compartido eso es
 * irrecuperable para quien no la creó.
 */

export function Drive() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const administra = tienePermiso(usuario, "drive:administrar")

  const [elegida, setElegida] = useState<number | null>(null)
  const [creando, setCreando] = useState(false)
  const [nombreNuevo, setNombreNuevo] = useState("")
  const [dentroDe, setDentroDe] = useState<number | null>(null)
  const [repartiendo, setRepartiendo] = useState(false)

  const carpetas = useQuery({
    queryKey: ["drive", "carpetas"],
    queryFn: () => api.get<Carpeta[]>("/api/v1/drive/carpetas"),
  })

  const raices = useMemo(() => armarArbol(carpetas.data ?? []), [carpetas.data])
  const carpeta = carpetas.data?.find((c) => c.id === elegida) ?? carpetas.data?.[0] ?? null

  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["drive", "carpetas"] })

  const crear = useMutation({
    mutationFn: () =>
      api.post<Carpeta>("/api/v1/drive/carpetas", {
        nombre: nombreNuevo,
        padre_id: dentroDe,
      }),
    onSuccess: (nueva) => {
      refrescar()
      setElegida(nueva.id)
      setCreando(false)
      setNombreNuevo("")
    },
  })

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/drive/carpetas/${id}`),
    onSuccess: () => {
      setElegida(null)
      refrescar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (nombreNuevo.trim()) crear.mutate()
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Drive</h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Manuales, formatos y actas de la agencia. Cada carpeta la abre quien tenga su acceso.
          </p>
        </div>
        {administra && (
          <Boton
            variante="primario"
            onClick={() => {
              setDentroDe(null)
              setCreando(true)
            }}
          >
            <FolderPlus size={16} aria-hidden="true" />
            Nueva carpeta
          </Boton>
        )}
      </header>

      {carpetas.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar las carpetas
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(200px,280px)_1fr]">
        <nav className="h-fit superficie p-2">
          <ArbolCarpetas raices={raices} elegida={carpeta?.id ?? null} onElegir={setElegida} />
        </nav>

        <section className="superficie p-4">
          {carpeta === null ? (
            <div
              className="flex flex-col items-center gap-2 py-12"
              style={{ color: "var(--fg-muted)" }}
            >
              <FolderOpen size={22} aria-hidden="true" />
              <p className="text-sm">
                {administra
                  ? "Crea la primera carpeta para empezar"
                  : "Todavía no tienes ninguna carpeta asignada"}
              </p>
            </div>
          ) : (
            <>
              {administra && (
                <div
                  className="mb-4 flex flex-wrap items-center gap-2 border-b pb-3"
                  style={{ borderColor: "var(--border)" }}
                >
                  <Insignia tono={carpeta.permisos_propios ? "primario" : "neutro"}>
                    {carpeta.permisos_propios ? "Acceso propio" : "Hereda el acceso"}
                  </Insignia>
                  <Boton variante="fantasma" tamano="sm" onClick={() => setRepartiendo(true)}>
                    <KeyRound size={14} aria-hidden="true" />
                    Quién la abre
                  </Boton>
                  <Boton
                    variante="fantasma"
                    tamano="sm"
                    onClick={() => {
                      setDentroDe(carpeta.id)
                      setCreando(true)
                    }}
                  >
                    <FolderPlus size={14} aria-hidden="true" />
                    Subcarpeta
                  </Boton>
                  <Boton
                    variante="fantasma"
                    tamano="sm"
                    onClick={() => borrar.mutate(carpeta.id)}
                    disabled={borrar.isPending}
                    style={{ color: "var(--destructive)" }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    Borrar carpeta
                  </Boton>
                </div>
              )}

              {borrar.isError && (
                <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
                  {borrar.error instanceof ErrorApi
                    ? borrar.error.message
                    : "No se pudo borrar la carpeta"}
                </p>
              )}

              <PanelArchivos carpetaId={carpeta.id} nombre={carpeta.nombre} />
            </>
          )}
        </section>
      </div>

      <Modal
        abierto={creando}
        titulo={dentroDe === null ? "Nueva carpeta" : "Nueva subcarpeta"}
        descripcion="Nace sin acceso para nadie: hereda el de su carpeta madre, o queda sólo para quien administra."
        onCerrar={() => setCreando(false)}
        ancho="sm"
      >
        <form onSubmit={enviar}>
          <label htmlFor="nombre-carpeta" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="nombre-carpeta"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            maxLength={120}
            className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--fg)",
            }}
          />
          {crear.isError && (
            <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
              {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Boton variante="fantasma" onClick={() => setCreando(false)}>
              Cancelar
            </Boton>
            <Boton variante="primario" type="submit" disabled={crear.isPending}>
              {crear.isPending ? "Creando…" : "Crear"}
            </Boton>
          </div>
        </form>
      </Modal>

      {repartiendo && carpeta && (
        <AccesosDeCarpeta carpeta={carpeta} onCerrar={() => setRepartiendo(false)} />
      )}
    </div>
  )
}
